import '../../i18n';
import {useTranslation} from 'react-i18next';
import React from 'react';
import styled from 'styled-components';
import { useMutation, useQuery } from '@apollo/client';
import { format } from 'date-fns';
import Modal from './Modal';
import { QUERY, MUTATION } from '../../gql-operations';
import { WHITE, PRIMARY_COLOR, GREEN } from '../../appearance/Colors';

const modalStyle = {
    content: {
        top: '50%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        color: WHITE,
        width: 'min(1040px, 94vw)',
        maxHeight: '84vh',
        borderRadius: 12,
        border: 0,
        background: '#222729',
        padding: 0,
        overflow: 'hidden',
        boxShadow: '0 18px 50px rgba(0, 0, 0, 0.34)'
    }
};

const formatRevisionDate = value => {
    if (!value) return 'Unknown time';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return 'Unknown time';
    return format(date, 'MMM d, yyyy HH:mm');
};

const getRevisionAttachmentLabel = (media, fallback) =>
    media?.displayName || media?.name || fallback;

const isImageLikeMedia = media => {
    const type = String(media?.type || '').toLowerCase();
    const mime = String(media?.mime || '').toLowerCase();
    return type.includes('image') || mime.includes('image/');
};

const getRevisionImagePreviewMedia = snapshot => {
    if (snapshot?.image) {
        return snapshot.image;
    }

    if (isImageLikeMedia(snapshot?.file)) {
        return snapshot.file;
    }

    return null;
};

const getRevisionImagePreviewUrl = media => media?.url || media?.downloadUrl || null;

function MessageVersionHistoryModal({ isOpen, message, onClose, onRestored, showDefaultNotification }) {
    const {t: lnT} = useTranslation();

    const messageId = message?.id;
    const isServerBacked = messageId && !String(messageId).startsWith('temp-') && !String(messageId).startsWith('x-');
    const [selectedRevisionId, setSelectedRevisionId] = React.useState(null);

    const {
        data,
        loading,
        error,
        fetchMore,
        refetch
    } = useQuery(QUERY.GET_MESSAGE_REVISIONS, {
        variables: {
            data: {
                messageId,
                limit: 20
            }
        },
        skip: !isOpen || !isServerBacked,
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: true
    });

    const [restoreMessageRevision, { loading: restoreLoading }] = useMutation(MUTATION.RESTORE_MESSAGE_REVISION);

    const edges = data?.messageRevisions?.edges || [];
    const pageInfo = data?.messageRevisions?.pageInfo;
    const selectedRevision =
        edges.find(edge => edge?.node?.id === selectedRevisionId)?.node ||
        edges[0]?.node ||
        null;
    const selectedFile = selectedRevision?.snapshot?.file || null;
    const selectedPreviewMedia = getRevisionImagePreviewMedia(selectedRevision?.snapshot);
    const selectedImagePreviewUrl = getRevisionImagePreviewUrl(selectedPreviewMedia);

    React.useEffect(() => {
        if (!isOpen) {
            setSelectedRevisionId(null);
            return;
        }

        if (!selectedRevisionId && edges[0]?.node?.id) {
            setSelectedRevisionId(edges[0].node.id);
        }
    }, [isOpen, edges, selectedRevisionId]);

    const handleLoadMore = async () => {
        if (!pageInfo?.hasMore || !pageInfo?.nextCursor) {
            return;
        }

        await fetchMore({
            variables: {
                data: {
                    messageId,
                    limit: 20,
                    cursor: pageInfo.nextCursor
                }
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult?.messageRevisions) {
                    return previousResult;
                }

                return {
                    ...previousResult,
                    messageRevisions: {
                        ...fetchMoreResult.messageRevisions,
                        edges: [
                            ...(previousResult?.messageRevisions?.edges || []),
                            ...(fetchMoreResult?.messageRevisions?.edges || [])
                        ]
                    }
                };
            }
        });
    };

    const handleRestore = async () => {
        if (!selectedRevision?.id || !messageId) {
            return;
        }

        try {
            const response = await restoreMessageRevision({
                variables: {
                    data: {
                        revisionId: selectedRevision.id,
                        messageId
                    }
                }
            });

            const restoredMessage = response?.data?.restoreMessageRevision;
            if (restoredMessage) {
                if (typeof onRestored === 'function') {
                    onRestored(restoredMessage);
                }
                await refetch({
                    data: {
                        messageId,
                        limit: 20
                    }
                });
                if (typeof showDefaultNotification === 'function') {
                    showDefaultNotification(messageId, 'Version restored.');
                }
            }
        } catch (error) {
            if (typeof showDefaultNotification === 'function') {
                showDefaultNotification(
                    messageId,
                    error?.graphQLErrors?.[0]?.message || error?.message || 'Failed to restore version.'
                );
            }
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onClose}
            style={modalStyle}
            overlayClassName="Overlay"
            contentLabel={lnT('messages:messageVersionHistory')}
        >
            <Container>
                <Sidebar>
                    <Header>{lnT('messages:versionHistory')}</Header>
                    <List>
                        {!isServerBacked ? (
                            <EmptyState>{lnT('messages:thisMessageDoesNotSupportVersionHistory')}</EmptyState>
                        ) : null}
                        {isServerBacked && loading && edges.length === 0 ? <EmptyState>{lnT('messages:loadingVersions')}</EmptyState> : null}
                        {isServerBacked && error ? (
                            <EmptyState>{lnT('messages:failedToLoadVersions')}{error?.graphQLErrors?.[0]?.message || error?.message || lnT('common:unknownError')}
                            </EmptyState>
                        ) : null}
                        {isServerBacked && !loading && !error && edges.length === 0 ? <EmptyState>{lnT('messages:noVersionsYet')}</EmptyState> : null}
                        {edges.map(edge => {
                            const revision = edge?.node;
                            if (!revision) return null;

                            return (
                                <RevisionRow
                                    key={revision.id}
                                    type="button"
                                    $isActive={selectedRevision?.id === revision.id}
                                    onClick={() => setSelectedRevisionId(revision.id)}
                                >
                                    <RevisionDate>{formatRevisionDate(revision.createdAt)}</RevisionDate>
                                    <RevisionMeta>
                                        {revision.createdBy === null ? lnT('folders:anonymousVisitor') : (revision.createdByDisplay || lnT('messages:unknownUser'))} · {revision.reason}
                                    </RevisionMeta>
                                    <RevisionNumber>{lnT('messages:v')}{revision.revisionNumber}</RevisionNumber>
                                </RevisionRow>
                            );
                        })}
                    </List>
                    {pageInfo?.hasMore ? (
                        <SidebarFooter>
                            <LoadMoreButton type="button" onClick={handleLoadMore}>{lnT('messages:loadMore')}</LoadMoreButton>
                        </SidebarFooter>
                    ) : null}
                </Sidebar>

                <PreviewPane>
                    <PreviewHeader>
                        <PreviewTitle>{lnT('folders:preview')}</PreviewTitle>
                        <PreviewActions>
                            <SecondaryButton type="button" onClick={onClose}>{lnT('common:close')}</SecondaryButton>
                            <RestoreButton
                                type="button"
                                onClick={handleRestore}
                                disabled={!selectedRevision || restoreLoading}
                            >
                                {restoreLoading ? lnT('messages:restoring') : lnT('messages:restoreThisVersion')}
                            </RestoreButton>
                        </PreviewActions>
                    </PreviewHeader>

                    {selectedRevision ? (
                        <PreviewContent>
                            <PreviewMeta>
                                {formatRevisionDate(selectedRevision.createdAt)} · {selectedRevision.createdBy === null ? lnT('folders:anonymousVisitor') : (selectedRevision.createdByDisplay || lnT('messages:unknownUser'))}
                            </PreviewMeta>
                            <Section>
                                <SectionLabel>{lnT('messages:title_7e8cd205')}</SectionLabel>
                                <SectionValue>{selectedRevision.snapshot?.title || lnT('messages:untitled')}</SectionValue>
                            </Section>
                            <Section>
                                <SectionLabel>{lnT('messages:note')}</SectionLabel>
                                <BodyValue>{selectedRevision.snapshot?.body || lnT('messages:noNote')}</BodyValue>
                            </Section>
                            <Section>
                                <SectionLabel>{lnT('messages:image')}</SectionLabel>
                                {selectedImagePreviewUrl ? (
                                    <ImagePreviewCard href={selectedImagePreviewUrl} target="_blank" rel="noreferrer">
                                        <RevisionImagePreview
                                            src={selectedImagePreviewUrl}
                                            alt={getRevisionAttachmentLabel(selectedPreviewMedia, 'Revision image')}
                                        />
                                    </ImagePreviewCard>
                                ) : null}
                                <AttachmentValue>{getRevisionAttachmentLabel(selectedPreviewMedia, 'No image')}</AttachmentValue>
                                {selectedPreviewMedia?.downloadUrl ? (
                                    <AttachmentActions>
                                        <AttachmentLink
                                            href={selectedPreviewMedia.downloadUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >{lnT('messages:downloadImage_3ac575dc')}</AttachmentLink>
                                    </AttachmentActions>
                                ) : null}
                            </Section>
                            <Section>
                                <SectionLabel>{lnT('messages:file')}</SectionLabel>
                                <AttachmentValue>{getRevisionAttachmentLabel(selectedFile, 'No file')}</AttachmentValue>
                                {selectedFile?.downloadUrl ? (
                                    <AttachmentActions>
                                        <AttachmentLink
                                            href={selectedFile.downloadUrl}
                                            target="_blank"
                                            rel="noreferrer"
                                        >{lnT('messages:downloadFile')}</AttachmentLink>
                                    </AttachmentActions>
                                ) : null}
                            </Section>
                        </PreviewContent>
                    ) : (
                        <PreviewEmpty>{lnT('messages:selectAVersionToPreview')}</PreviewEmpty>
                    )}
                </PreviewPane>
            </Container>
        </Modal>
    );
}

const Container = styled.div`
    display: grid;
    grid-template-columns: 320px minmax(0, 1fr);
    height: min(760px, 84vh);
    min-height: 520px;
    overflow: hidden;

    @media (max-width: 720px) {
        grid-template-columns: 1fr;
        height: min(820px, 88vh);
    }
`;

const Sidebar = styled.div`
    border-right: 1px solid rgba(255, 255, 255, 0.08);
    background: rgba(255, 255, 255, 0.02);
    display: flex;
    flex-direction: column;
    min-height: 0;

    @media (max-width: 720px) {
        max-height: 230px;
        border-right: 0;
        border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    }
`;

const Header = styled.div`
    min-height: 52px;
    padding: 8px 12px 4px;
    display: flex;
    align-items: center;
    font-size: 15px;
    font-weight: 600;
    color: ${WHITE};
`;

const List = styled.div`
    flex: 1;
    padding: 4px 12px 8px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow-y: auto;
    min-height: 0;
`;

const RevisionRow = styled.button`
    text-align: left;
    border: 1px solid ${({ $isActive }) => ($isActive ? PRIMARY_COLOR : 'rgba(255,255,255,0.14)')};
    background: ${({ $isActive }) => ($isActive ? 'rgba(73, 153, 255, 0.14)' : 'rgba(255,255,255,0.04)')};
    border-radius: 14px;
    padding: 10px 12px;
    cursor: pointer;
    color: ${WHITE};
`;

const RevisionDate = styled.div`
    font-size: 15px;
    font-weight: 600;
    margin-bottom: 4px;
`;

const RevisionMeta = styled.div`
    font-size: 13px;
    color: rgba(255,255,255,0.68);
`;

const RevisionNumber = styled.div`
    font-size: 12px;
    color: rgba(255,255,255,0.5);
    margin-top: 8px;
`;

const SidebarFooter = styled.div`
    padding: 0 12px 12px;
`;

const LoadMoreButton = styled.button`
    width: 100%;
    border: none;
    border-radius: 12px;
    padding: 12px;
    background: rgba(255,255,255,0.08);
    color: ${WHITE};
    cursor: pointer;
`;

const PreviewPane = styled.div`
    display: flex;
    flex-direction: column;
    min-width: 0;
    min-height: 0;
    overflow: hidden;
`;

const PreviewHeader = styled.div`
    min-height: 52px;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(255,255,255,0.08);
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
`;

const PreviewTitle = styled.div`
    font-size: 15px;
    font-weight: 600;
    color: ${WHITE};
`;

const PreviewActions = styled.div`
    display: flex;
    gap: 6px;
`;

const SecondaryButton = styled.button`
    border: none;
    border-radius: 9px;
    padding: 7px 10px;
    background: transparent;
    border: 1px solid rgba(255,255,255,0.14);
    color: ${WHITE};
    cursor: pointer;
`;

const RestoreButton = styled.button`
    border: none;
    border-radius: 9px;
    padding: 7px 10px;
    background: ${GREEN};
    color: white;
    cursor: pointer;
    font-weight: 600;

    &:disabled {
        opacity: 0.6;
        cursor: default;
    }
`;

const PreviewContent = styled.div`
    flex: 1;
    min-height: 0;
    padding: 8px 12px 12px;
    overflow-y: auto;
    overscroll-behavior: contain;
`;

const PreviewMeta = styled.div`
    font-size: 13px;
    color: rgba(255,255,255,0.62);
    margin-bottom: 10px;
`;

const Section = styled.div`
    margin-bottom: 8px;
    padding: 10px 12px;
    border: 1px solid rgba(255,255,255,0.14);
    border-radius: 14px;
    background: rgba(255,255,255,0.025);
`;

const SectionLabel = styled.div`
    font-size: 12px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: rgba(255,255,255,0.48);
    margin-bottom: 6px;
`;

const SectionValue = styled.div`
    font-size: 16px;
    color: ${WHITE};
`;

const BodyValue = styled.div`
    font-size: 14px;
    line-height: 1.55;
    white-space: pre-wrap;
    color: rgba(255,255,255,0.86);
`;

const AttachmentValue = styled.div`
    font-size: 14px;
    color: rgba(255,255,255,0.8);
`;

const AttachmentActions = styled.div`
    display: flex;
    gap: 10px;
    margin-top: 10px;
`;

const AttachmentLink = styled.a`
    display: inline-flex;
    align-items: center;
    border-radius: 10px;
    padding: 9px 12px;
    background: rgba(73, 153, 255, 0.14);
    border: 1px solid rgba(73, 153, 255, 0.26);
    color: ${WHITE};
    text-decoration: none;
    font-size: 13px;
    font-weight: 600;
`;

const ImagePreviewCard = styled.a`
    display: block;
    width: min(100%, 520px);
    border-radius: 16px;
    overflow: hidden;
    background: rgba(255,255,255,0.04);
    border: 1px solid rgba(255,255,255,0.08);
    margin-bottom: 10px;
`;

const RevisionImagePreview = styled.img`
    display: block;
    width: 100%;
    max-height: 320px;
    object-fit: contain;
    background: rgba(0,0,0,0.28);
`;

const PreviewEmpty = styled.div`
    padding: 12px;
    color: rgba(255,255,255,0.64);
`;

const EmptyState = styled.div`
    padding: 12px;
    color: rgba(255,255,255,0.62);
`;

export default MessageVersionHistoryModal;
